import * as React from 'react'
import {
  useGetIngestJobsPageQuery,
  useListIngestMappingConfigsQuery,
  type IngestMappingConfigSummary
} from '@axilon-apps/domains-ontology-ui-state'
import { ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { formatRelativeTime } from '../../../lib/time'
import { tabularConfigPath } from '../../TabularIngestJobPage/route'

export interface SelectedTabularConfig {
  id: string
  name: string
  version: number
  parserKind: 'script' | 'agent'
  definition: IngestMappingConfigSummary['definition']
}

interface ConfigPickerProps {
  value?: { id?: string; name?: string; version?: number }
  onSelect: (config: SelectedTabularConfig) => void
}

function selectedConfig(config: IngestMappingConfigSummary): SelectedTabularConfig {
  return {
    id: config.id,
    name: config.name,
    version: config.version,
    parserKind: config.definition.parser.kind,
    definition: config.definition
  }
}

export function ConfigPicker({ value, onSelect }: ConfigPickerProps) {
  const latestQuery = useListIngestMappingConfigsQuery({
    kind: 'mapped_tabular_ingest',
    latest: true,
    limit: 500
  })
  const versionsQuery = useListIngestMappingConfigsQuery(
    {
      kind: 'mapped_tabular_ingest',
      name: value?.name ?? '',
      limit: 500
    },
    { skip: !value?.name }
  )
  const runsQuery = useGetIngestJobsPageQuery({
    kind: 'mapped_tabular_ingest',
    page: 0,
    pageSize: 30
  })
  const configs = React.useMemo(
    () => [...(latestQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [latestQuery.data]
  )
  const lastRunByName = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const job of runsQuery.data?.jobs ?? []) {
      if (job.kind !== 'mapped_tabular_ingest' || !job.payload?.config.name) continue
      if (!map.has(job.payload.config.name)) map.set(job.payload.config.name, job.submittedAt)
    }
    return map
  }, [runsQuery.data])

  React.useEffect(() => {
    if (!value?.name || value.id || !latestQuery.data) return
    const match = latestQuery.data.find((config) => config.name === value.name)
    if (match) onSelect(selectedConfig(match))
  }, [latestQuery.data, onSelect, value?.id, value?.name])

  const current =
    (versionsQuery.data ?? []).find(
      (config) => config.id === value?.id || config.version === value?.version
    ) ?? (latestQuery.data ?? []).find((config) => config.id === value?.id)

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tabular-config-name" className="text-11 font-medium text-ink-2">
          Config
        </Label>
        <Select
          value={current?.name ?? value?.name ?? ''}
          onValueChange={(name) => {
            const config = (latestQuery.data ?? []).find((candidate) => candidate.name === name)
            if (config) onSelect(selectedConfig(config))
          }}
        >
          <SelectTrigger id="tabular-config-name" aria-label="Tabular config">
            <SelectValue
              placeholder={latestQuery.isLoading ? 'Loading configs…' : 'Select a config'}
            />
          </SelectTrigger>
          <SelectContent>
            {configs.map((config) => {
              const lastRun = lastRunByName.get(config.name)
              return (
                <SelectItem key={config.id} value={config.name}>
                  {config.name} · v{config.version} · {config.definition.parser.kind} ·{' '}
                  {lastRun ? formatRelativeTime(lastRun) : 'no runs yet'}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
        {latestQuery.isError ? (
          <p role="alert" className="text-10 text-st-down">
            Could not load tabular configs.
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tabular-config-version" className="text-11 font-medium text-ink-2">
          Version
        </Label>
        <Select
          disabled={!value?.name || versionsQuery.isLoading}
          value={value?.version ? String(value.version) : ''}
          onValueChange={(version) => {
            const config = (versionsQuery.data ?? []).find(
              (candidate) => candidate.version === Number(version)
            )
            if (config) onSelect(selectedConfig(config))
          }}
        >
          <SelectTrigger id="tabular-config-version" aria-label="Config version">
            <SelectValue placeholder="Version" />
          </SelectTrigger>
          <SelectContent>
            {[...(versionsQuery.data ?? [])]
              .sort((a, b) => b.version - a.version)
              .map((config) => (
                <SelectItem key={config.id} value={String(config.version)}>
                  v{config.version} · {config.definition.parser.kind}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
      {current ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3 sm:col-span-2">
          <span>
            v{current.version} · {current.definition.parser.kind} parser
          </span>
          <Link
            to={tabularConfigPath(current.name)}
            className="inline-flex items-center gap-1 text-st-info hover:underline"
          >
            View this config
            <ExternalLink className="size-3" aria-hidden />
          </Link>
        </div>
      ) : null}
    </div>
  )
}
